import {
  DataGrid,
  FilterToolbar,
  formatCurrency,
  formatDate,
  StatusBadge,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  getGetAdminAutoForceOutPlansQueryKey,
  getGetAdminAutoForceOutsQueryKey,
  useGetAdminAutoForceOutPlans,
  usePostAdminAutoForceOutPlanReprocess,
  type AdminAutoForceOutPlanRowRes,
  type GetAdminAutoForceOutPlansParams
} from '@ijoin/hooks';
import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { useState } from 'react';

import { AutoForceOutPlanActionsCell } from '../../components';
import { useAdvisoryOrganizationOptions } from '../../components/useAdvisoryOrganizationOptions';
import { ApiError } from '../../types';

const WHOLE_DOLLARS = { maximumFractionDigits: 0 };

const yesNo = (value: boolean | null) => (value ? 'Yes' : 'No');

export const Page = () => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState<GridQuery>();

  const plans = useGetAdminAutoForceOutPlans(
    query as GetAdminAutoForceOutPlansParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const orgs = useAdvisoryOrganizationOptions();

  const reprocess = usePostAdminAutoForceOutPlanReprocess<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: res => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminAutoForceOutPlansQueryKey()
        });
        queryClient.invalidateQueries({
          queryKey: getGetAdminAutoForceOutsQueryKey()
        });
        notifications.success(
          `${res.reprocessed} force out candidates queued for reprocessing${res.skipped ? `; ${res.skipped} skipped because the participant already has an active candidate` : ''}`
        );
      }
    }
  });

  const columns: GridColDef<AdminAutoForceOutPlanRowRes>[] = [
    {
      field: 'planDescription',
      flex: 1.4,
      headerName: 'Plan',
      minWidth: 220,
      sortable: false
    },
    {
      field: 'rolloverProviderName',
      headerName: 'Provider',
      sortable: false,
      width: 140
    },
    {
      field: 'enableAutoForceOut',
      headerName: 'Enabled',
      renderCell: params => (
        <StatusBadge
          data-testid={`enabled-${params.row.planCorrelationId}`}
          severity={params.value ? 'success' : 'warning'}>
          {params.value ? 'Enabled' : 'Disabled'}
        </StatusBadge>
      ),
      sortable: false,
      width: 120
    },
    {
      field: 'vestedBalanceRange',
      headerName: 'Vested Balance Range',
      renderCell: params =>
        `${formatCurrency(params.row.forceOutMinVestedBalanceThreshold ?? 0, WHOLE_DOLLARS)} to ${formatCurrency(params.row.forceOutMaxVestedBalanceThreshold ?? 0, WHOLE_DOLLARS)}`,
      sortable: false,
      width: 190
    },
    {
      field: 'forceOutCashThreshold',
      headerName: 'Cash Out Below',
      renderCell: params =>
        params.row.enableForceOutCashThreshold
          ? formatCurrency(params.value ?? 0, WHOLE_DOLLARS)
          : 'Off',
      sortable: false,
      width: 140
    },
    {
      field: 'forceOutExcludeRolloverBalance',
      headerName: 'Excludes Rollover',
      renderCell: params => yesNo(params.value),
      sortable: false,
      width: 150
    },
    {
      align: 'right',
      field: 'forceOutResponseDays',
      headerAlign: 'right',
      headerName: 'Response Days',
      sortable: false,
      width: 130
    },
    {
      field: 'forceOutLimitToPriorYearEnd',
      headerName: 'Prior Year End Only',
      renderCell: params => yesNo(params.value),
      sortable: false,
      width: 160
    },
    {
      field: 'recordkeeperSendsHardCopyMailing',
      headerName: 'RK Mails Notice',
      renderCell: params => yesNo(params.value),
      sortable: false,
      width: 140
    },
    {
      field: 'lastIdentificationSweepAt',
      headerName: 'Last Identify Sweep',
      renderCell: params => formatDate(params.value),
      sortable: false,
      width: 190
    },
    {
      align: 'right',
      field: 'candidateCount',
      headerAlign: 'right',
      headerName: 'Candidates',
      sortable: false,
      width: 110
    },
    {
      align: 'right',
      field: 'stalledCount',
      headerAlign: 'right',
      headerName: 'Stalled',
      renderCell: params => (
        <StatusBadge
          data-testid={`stalled-${params.row.planCorrelationId}`}
          severity={params.value ? 'error' : 'success'}>
          {params.value}
        </StatusBadge>
      ),
      sortable: false,
      width: 100
    },
    {
      align: 'right',
      field: 'reprocessableCount',
      headerAlign: 'right',
      headerName: 'Reprocessable',
      sortable: false,
      width: 130
    },
    {
      field: 'updatedAt',
      headerName: 'Settings Updated',
      renderCell: params => formatDate(params.value),
      sortable: false,
      width: 150
    },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <AutoForceOutPlanActionsCell
          onReprocess={() =>
            reprocess.mutate({
              planCorrelationId: params.row.planCorrelationId
            })
          }
          planCorrelationId={params.row.planCorrelationId}
          planDescription={params.row.planDescription ?? ''}
          reprocessableCount={params.row.reprocessableCount}
        />
      ),
      sortable: false,
      width: 70
    }
  ];

  const filterDefs: FilterDef[] = [
    {
      isLoading: orgs.isFetching,
      label: 'Advisory Organization',
      name: 'advisoryOrganizationId',
      options: orgs.data ?? [],
      type: 'autocomplete'
    },
    { column: 'planDescription', label: 'Plan', name: 'plan', type: 'text' },
    {
      label: 'Enabled',
      name: 'enableAutoForceOut',
      options: [
        { label: 'Enabled', value: 'true' },
        { label: 'Disabled', value: 'false' }
      ],
      type: 'select'
    }
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filterDefs}
        getRowId={row => row.planCorrelationId}
        loading={!query || plans.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{ left: ['planDescription'], right: ['actions'] }}
        rowCount={plans.data?.pagination?.total ?? 0}
        rows={plans.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          noRowsOverlay: {
            message: 'No plans with force out settings match this search'
          },
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'autoForceOutPlans'
          },
          toolbar: {
            onRefresh: plans.refetch,
            refreshTestId: 'autoForceOutPlansRefresh',
            searchButtonTestId: 'autoForceOutPlansSearch',
            triggerTestId: 'autoForceOutPlansFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

export const Route = createFileRoute('/auto-force-outs/')({
  component: Page
});
